import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { getProduct, getFormFields } from '../api';
import { useSettings } from '../context/SettingsContext';
import { waLink, fillTemplate } from '../components/WhatsAppFloat';
import PageWrapper from '../components/PageWrapper';
import Loader from '../components/Loader';
import DynamicForm from '../components/DynamicForm';

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colorIdx, setColorIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    setLoading(true);
    getProduct(slug)
      .then((p) => { setProduct(p); setColorIdx(0); setImgIdx(0); })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    getFormFields(true).then(setFields).catch(() => {});
  }, [slug]);

  const colorsVisible =
    product && settings?.showProductColors !== false && product.showColors !== false && product.colors?.length > 0;
  const priceVisible = product && settings?.showProductPrice !== false && product.showPrice !== false;

  // Gallery = selected colour images (if colours shown) else general images.
  const gallery = useMemo(() => {
    if (!product) return [];
    const colImgs = colorsVisible ? product.colors?.[colorIdx]?.images || [] : [];
    const imgs = colImgs.length ? colImgs : product.images || [];
    return imgs.length ? imgs : ['https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?auto=format&fit=crop&w=800&q=80'];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, colorIdx, colorsVisible]);

  if (loading) return <PageWrapper><Loader /></PageWrapper>;
  if (!product) {
    return (
      <PageWrapper>
        <div className="empty" style={{ minHeight: '60vh' }}>
          <div className="empty__icon">😕</div>
          <h3>Product not found</h3>
          <Link to="/products" className="btn btn-ghost mt-2">Back to products</Link>
        </div>
      </PageWrapper>
    );
  }

  // Message template is authored by the admin (Settings → WhatsApp).
  const waProduct = waLink(
    settings?.whatsappNumber,
    fillTemplate(settings?.whatsappProductMessage || 'Hi! I am interested in "{product}". {url}', {
      product: product.name,
      color: colorsVisible && product.colors?.[colorIdx] ? `(${product.colors[colorIdx].name})` : '',
      url: window.location.href,
      site: settings?.siteName || '',
    })
  );

  return (
    <PageWrapper>
      <section className="section detail">
        <div className="container">
          <Link to="/products" className="muted" style={{ fontSize: 14 }}>← Back to products</Link>
          <div className="grid grid-2" style={{ marginTop: 20, alignItems: 'start' }}>
            {/* Gallery */}
            <div className="gallery">
              <div className="gallery__main">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={gallery[imgIdx]}
                    src={gallery[imgIdx]}
                    alt={product.name}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  />
                </AnimatePresence>
              </div>
              {gallery.length > 1 && (
                <div className="gallery__thumbs">
                  {gallery.map((g, i) => (
                    <div key={i} className={`gallery__thumb ${i === imgIdx ? 'active' : ''}`} onClick={() => setImgIdx(i)}>
                      <img src={g} alt={`${product.name} ${i + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {product.category && <span className="badge">{product.category}</span>}
              <h1 className="detail__title">{product.name}</h1>
              {product.description && <p className="prose">{product.description}</p>}

              {priceVisible && (
                <div className="detail__price">
                  <span className="now">₹{Number(product.price).toLocaleString('en-IN')}</span>
                  {product.mrp > product.price && (
                    <>
                      <span className="price"><small>₹{Number(product.mrp).toLocaleString('en-IN')}</small></span>
                      <span className="badge" style={{ background: 'rgba(22,163,74,.14)', color: 'var(--success)' }}>
                        {Math.round((1 - product.price / product.mrp) * 100)}% off
                      </span>
                    </>
                  )}
                </div>
              )}

              {colorsVisible && (
                <>
                  <div className="detail__section-h">Colour — {product.colors[colorIdx]?.name}</div>
                  <div className="color-opts">
                    {product.colors.map((c, i) => (
                      <button
                        key={i}
                        className={`color-opt ${i === colorIdx ? 'active' : ''}`}
                        onClick={() => { setColorIdx(i); setImgIdx(0); }}
                      >
                        <span className="dot" style={{ background: c.hex }} />
                        {c.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="flex gap wrap" style={{ margin: '26px 0' }}>
                <button className="btn btn-primary" onClick={() => setShowEnquiry(true)}>Enquire Now</button>
                {waProduct && (
                  <a href={waProduct} target="_blank" rel="noreferrer" className="btn" style={{ background: 'var(--whatsapp)', color: '#fff' }}>
                    <FaWhatsapp /> WhatsApp
                  </a>
                )}
              </div>

              {product.tags?.length > 0 && (
                <>
                  <div className="detail__section-h">Tags</div>
                  <div className="flex gap wrap">
                    {product.tags.map((t) => <span key={t} className="tag-chip">#{t}</span>)}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry modal */}
      <AnimatePresence>
        {showEnquiry && (
          <>
            <motion.div className="nav__overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowEnquiry(false)} />
            <motion.div
              className="glass"
              style={{ position: 'fixed', zIndex: 130, top: '50%', left: '50%', width: 'min(620px, 92vw)', maxHeight: '88vh', overflow: 'auto', padding: 28 }}
              initial={{ opacity: 0, y: 40, x: '-50%', translateY: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%', translateY: '-50%' }}
              exit={{ opacity: 0, y: 40, x: '-50%', translateY: '-50%' }}
            >
              <div className="between" style={{ marginBottom: 18 }}>
                <h3>Enquire about {product.name}</h3>
                <button className="share-btn" onClick={() => setShowEnquiry(false)}>✕</button>
              </div>
              <DynamicForm fields={fields} source="product" refId={product._id} refName={product.name} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}
